import {Button, closeCurrentDialog, openDeleteDialog, openDialog} from 'platform/components';
import {Heading, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useParams} from 'react-router-dom';

import {mergeAll} from 'ramda';
import {isArray, noop} from 'ramda-adjunct';

import {useDeleteCustomerContractMaterialDiscountMutation} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DmsDataGrid, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import type {QueryFilterObject, ActionCallback} from 'features/eag-grid';

import {MaterialDiscountForm} from './components/MaterialDiscountForm';

export function MaterialDiscountsTab() {
  const {id} = useParams();
  const [deleteMaterialDiscount] = useDeleteCustomerContractMaterialDiscountMutation();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {customerContractId: id ?? ''}]);

  const handleAdd = () =>
    openDialog(
      <MaterialDiscountForm
        customerContractId={id}
        onClose={closeCurrentDialog}
        afterSubmit={() => refreshDmsDatagrid('customer-contract-material-discount')}
        data-testid={testIds.settings.customerContractsDetail('materialDiscountForm')}
      />,
      {
        title: i18n.t('entity.customerContract.labels.newMaterialDiscount'),
        scrollBehavior: 'inside',
        size: 'large',
        withAdditionalFooter: true,
      }
    );

  const actionCallback: ActionCallback = ({actionKey, rowId}) => {
    const materialDiscountId = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        openDialog(
          <MaterialDiscountForm
            customerContractId={id}
            onClose={closeCurrentDialog}
            afterSubmit={() => refreshDmsDatagrid('customer-contract-material-discount')}
            materialDiscountId={materialDiscountId}
          />,
          {
            title: i18n.t('entity.customerContract.labels.editMaterialDiscount'),
            scrollBehavior: 'inside',
            size: 'large',
            withAdditionalFooter: true,
          }
        )
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteMaterialDiscount({customerContractId: id ?? '', materialDiscountId})
              .unwrap()
              .then(() => refreshDmsDatagrid('customer-contract-material-discount'))
              .catch(handleApiError),
        })
      )
      .otherwise(noop);
  };

  return (
    <VStack spacing={4}>
      <HStack justify="space-between" align="center">
        <Heading size={4}>{i18n.t('entity.customerContract.labels.materialDiscounts')}</Heading>
        <Button
          title={i18n.t('general.actions.add')}
          variant="link"
          leftIcon="content/add_circle"
          size="small"
          onClick={handleAdd}
          data-testid={testIds.settings.customerContractsDetail('addMaterialDiscount')}
        />
      </HStack>
      <DmsDataGrid
        autoHeight
        gridCode="customer-contract-material-discount"
        queryModifier={queryModifier}
        actionCallback={actionCallback}
        data-testid={testIds.settings.customerContractsDetail('materialDG')}
      />
    </VStack>
  );
}
